import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  CreateCategoryDTO,
  UpdateCategoryDTO,
} from "@/modules/form-center/domain/contracts/categories";
import { Button } from "@devhop/ui";
import { useNavigate, useParams, useRouterState } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle } from "lucide-react";
import {
  useCategoryQuery,
  useCreateCategory,
  useUpdateCategoryAny,
} from "../api/categories.queries";
import {
  CategoryFormTour,
  useCategoryFormTour,
} from "../tour/CategoryFormTour";
import { CategoryForm } from "../ui/CategoryForm";

export function CategoryFormPage() {
  const nav = useNavigate();
  const router = useRouterState();
  const params = useParams({ strict: false }) as { id?: string };
  const isEdit = !!params.id && router.location.pathname.includes("/edit");

  const { data: category, isLoading: isLoadingCategory } = useCategoryQuery(
    params.id ?? "",
  );
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategoryAny();
  const tour = useCategoryFormTour();

  const handleSubmit = async (
    values: CreateCategoryDTO | UpdateCategoryDTO,
  ) => {
    if (isEdit && params.id) {
      await updateCategory.mutateAsync({
        id: params.id,
        ...values,
      });
    } else {
      await createCategory.mutateAsync(values as CreateCategoryDTO);
    }
    nav({
      to: "/app/forms/categories",
      search: {
        offset: 0,
        limit: 20,
        sort: undefined,
        filters: undefined,
      },
    });
  };

  if (isEdit && isLoadingCategory) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">ກຳລັງໂຫຼດ...</div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <CategoryFormTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />

        <div className="mb-6">
          <div className="mb-4 flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h1 className="font-bold text-2xl tracking-tight">
                  {isEdit ? "ແກ້ໄຂຫມວດຫມູ່" : "ສ້າງຫມວດຫມູ່ໃໝ່"}
                </h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground">
                {isEdit ? "ແກ້ໄຂຂໍ້ມູນຫມວດຫມູ່ຟອມ" : "ສ້າງຫມວດຫມູ່ຟອມໃໝ່ເພື່ອຈັດການຟອມ"}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                nav({
                  to: "/app/forms/categories",
                  search: {
                    offset: 0,
                    limit: 20,
                    sort: undefined,
                    filters: undefined,
                  },
                })
              }
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <div className="rounded-xl border bg-card p-6">
          <CategoryForm
            initialValues={
              isEdit && category
                ? {
                    name: category.name,
                    description: category.description ?? "",
                    icon: category.icon ?? "",
                    orderIndex: category.orderIndex,
                    isActive: category.isActive,
                  }
                : undefined
            }
            onSubmit={handleSubmit}
            submitting={
              isEdit ? updateCategory.isPending : createCategory.isPending
            }
          />
        </div>
      </Main>
    </>
  );
}
